import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Pagination, Typography, useTheme } from '@mui/material';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../components/shared/useUser';
import { StorefrontBanner } from '../ContentManagement/Banners/types/BannerAdmin';
import { bannerService } from '../../app/services/api/content/bannerService';
import { client, Category, Item, OrderlySystem } from '../../app/services/api/orderManagementClient';
import dayjs from 'dayjs';
import { useActiveOrder } from '../../contexts/ActiveOrderContext';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import StorefrontHeader from './components/StorefrontHeader';
import CategorySidebar from './components/CategorySidebar';
import HeroBannerCarousel from './components/HeroBannerCarousel';
import PromoBannerCard from './components/PromoBannerCard';
import ProductCard from './components/ProductCard';
import { BANNER_DESIGN_HEIGHT, BANNER_DESIGN_WIDTH } from '../ContentManagement/Banners/components/BannerPreview';

const PAGE_SIZE = 12;
const STOREFRONT_HEADER_HEIGHT = 44;
const HERO_BANNER_FIXED_HEIGHT = BANNER_DESIGN_HEIGHT;
const HERO_SECTION_GAP = 16;

const StorefrontPage: FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('storefront');
  const { selectedStore } = useContext(UserContext);
  const { activeOrder, setActiveOrder } = useActiveOrder();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  const [banners, setBanners] = useState<StorefrontBanner[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [products, setProducts] = useState<Item[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotalCount, setProductTotalCount] = useState(0);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [orderTypeId, setOrderTypeId] = useState<number>(-1);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const initialised = useRef(false);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    bannerService.storefront.getBanners()
      .then(setBanners)
      .catch(console.error);
  }, []);

  useEffect(() => {
    const handleFocus = async () => {
      const storedId = sessionStorage.getItem('orderly_active_order_meta');
      if (!storedId || !selectedStore?.storeNumber) return;
      const orderId = parseInt(storedId, 10);
      const refreshed = await client.getOrder(selectedStore.storeNumber, orderId);
      if (refreshed.success && refreshed.data) {
        setActiveOrder(refreshed.data);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [selectedStore?.storeNumber, setActiveOrder]);

  useEffect(() => {
    if (!selectedStore?.storeNumber) return;
    if (initialised.current) return;
    initialised.current = true;

    const init = async () => {
      try {
        const orderTypesRes = await client.getOrderTypes(selectedStore.storeNumber);
        const types = orderTypesRes.data ?? [];
        const defaultType = types.length > 0 ? types[0].id : -1;
        setOrderTypeId(defaultType);

        setCategoriesLoading(true);
        const categoriesRes = await client.getCategories(selectedStore.storeNumber, defaultType, '');
        setCategories(categoriesRes.data ?? []);
        setCategoriesLoading(false);

        await fetchProducts(selectedStore.storeNumber!, '', null, defaultType);

        if (!activeOrder) {
          try {
            const storedId = sessionStorage.getItem('orderly_active_order_meta');
            if (storedId) {
              const orderId = parseInt(storedId, 10);
              const restored = await client.getOrder(selectedStore.storeNumber, orderId);
              if (restored.success && restored.data) {
                setActiveOrder(restored.data);
              } else {
                sessionStorage.removeItem('orderly_active_order_meta');
              }
            }
          } catch {
            sessionStorage.removeItem('orderly_active_order_meta');
          }
        }
      } catch (err) {
        console.error('Failed to initialise storefront', err);
        setCategoriesLoading(false);
      }
    };
    init();
  }, [selectedStore?.storeNumber]);

  const fetchProducts = async (storeNumber: string, search: string, categoryId: number | null, orderType: number, page: number = 1) => {
    setProductsLoading(true);
    try {
      const res = await client.getItems({
        storeNumber,
        item: search || undefined,
        categoryId: categoryId ?? -1,
        orderType,
        supplier: undefined,
        pageNumber: page,
        pageSize: PAGE_SIZE,
        sort: 'Name',
        direction: 'asc',
      });
      setProducts(res.data?.items ?? []);
      setProductPage(res.data?.pageNumber ?? page);
      setProductTotalPages(res.data?.totalPages ?? 1);
      setProductTotalCount(res.data?.totalCount ?? 0);
    } catch (err) {
      console.error('Failed to fetch products', err);
      setProducts([]);
      setProductPage(1);
      setProductTotalPages(1);
      setProductTotalCount(0);
    } finally {
      setProductsLoading(false);
    }
  };

  const handleCategorySelect = useCallback((catId: number | null) => {
    setSelectedCategoryId(catId);
    if (selectedStore?.storeNumber) {
      fetchProducts(selectedStore.storeNumber, searchValue, catId, orderTypeId, 1);
    }
  }, [selectedStore?.storeNumber, searchValue, orderTypeId]);

  const handleSearch = useCallback(() => {
    if (selectedStore?.storeNumber) {
      fetchProducts(selectedStore.storeNumber, searchValue, selectedCategoryId, orderTypeId, 1);
    }
  }, [selectedStore?.storeNumber, searchValue, selectedCategoryId, orderTypeId]);

  const handleProductsPageChange = useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    if (!selectedStore?.storeNumber) return;
    fetchProducts(selectedStore.storeNumber, searchValue, selectedCategoryId, orderTypeId, page);
  }, [selectedStore?.storeNumber, searchValue, selectedCategoryId, orderTypeId]);

  const resolveOrderForAdd = useCallback(async (): Promise<typeof activeOrder> => {
    if (!selectedStore?.storeNumber) return null;

    if (activeOrder) return activeOrder;

    if (orderTypeId < 0) return null;

    const today = dayjs().format('YYYY-MM-DD');
    const orderlySystem = OrderlySystem.OFD;

    const created = await client.createOrder({
      storeNumber: selectedStore.storeNumber,
      emergencyOrder: true,
      orderTypeId,
      deliveryDate: today,
      cutOffDate: today,
      supplierNumber: '',
      orderlySystem,
      items: [],
    });

    if (!created.success || !created.data) return null;

    const createdOrder = await client.getOrder(selectedStore.storeNumber, created.data);
    if (!createdOrder.success || !createdOrder.data) return null;

    setActiveOrder(createdOrder.data);
    return createdOrder.data;
  }, [activeOrder, orderTypeId, selectedStore?.storeNumber, setActiveOrder]);

  const handleAddToOrder = useCallback(async (product: Item) => {
    if (!selectedStore?.storeNumber) return;

    try {
      const order = await resolveOrderForAdd();
      if (!order) {
        setSnackbarMessage(t('addToDeliveryError'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const step = Math.max(1, product.minQuantity ?? 1);
      const existingItem = order.items?.find((i) => i.itemId === product.id);

      let apiResult: { success: boolean; message?: string | undefined };

      if (existingItem) {
        const newQuantity = existingItem.quantity + step;
        const updateRequest = {
          id: order.id,
          orderlySystem: OrderlySystem.OFD,
          items: order.items.map((i) => ({
            id: i.itemId,
            quantity: i.itemId === product.id ? newQuantity : i.quantity,
          })),
        };
        apiResult = await client.updateOrder(selectedStore.storeNumber, updateRequest);
      } else {
        apiResult = await client.addOrderItem(selectedStore.storeNumber, {
          orderId: order.id,
          itemId: product.id,
          quantity: step,
          predictedQuantity: step,
        });
      }

      if (!apiResult.success) {
        setSnackbarMessage(apiResult.message ?? t('addToDeliveryError'));
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      const refreshedOrder = await client.getOrder(selectedStore.storeNumber, order.id);
      if (refreshedOrder.success && refreshedOrder.data) {
        setActiveOrder(refreshedOrder.data);
      }
      setSnackbarMessage(t('addToDeliverySuccess'));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(t('addToDeliveryError'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [resolveOrderForAdd, selectedStore?.storeNumber, setActiveOrder]);

  const handleCartClick = () => {
    if (!activeOrder) return;
    navigate(`/order/${activeOrder.id}`);
  };

  const cartItemCount = activeOrder?.items?.length ?? 0;

  const slotGroups = banners.reduce<Record<string, typeof banners>>((acc, b) => {
    if (!acc[b.slotKey]) acc[b.slotKey] = [];
    acc[b.slotKey].push(b);
    return acc;
  }, {});
  const sortedSlotKeys = Object.keys(slotGroups).sort(
    (a, b) => (slotGroups[b][0].width ?? 0) - (slotGroups[a][0].width ?? 0),
  );
  const heroBanners = sortedSlotKeys.length > 0 ? slotGroups[sortedSlotKeys[0]] : [];
  const promoLeftBanners = sortedSlotKeys.length > 1 ? slotGroups[sortedSlotKeys[1]] : [];
  const promoRightBanners = sortedSlotKeys.length > 2 ? slotGroups[sortedSlotKeys[2]] : [];
  const hero0 = heroBanners[0];
  const heroHeight = heroBanners.length > 0
    ? (hero0.width > 0 && hero0.height > 0
      ? Math.round(BANNER_DESIGN_WIDTH * hero0.height / hero0.width)
      : hero0.height || HERO_BANNER_FIXED_HEIGHT)
    : HERO_BANNER_FIXED_HEIGHT;
  const sidebarFixedHeight = heroBanners.length > 0
    ? STOREFRONT_HEADER_HEIGHT + HERO_SECTION_GAP + heroHeight
    : undefined;

  return (
    <Box sx={{ background: 'linear-gradient(180deg, #EFEFEF 0px, #FFFFFF 220px)', minHeight: '100vh', p: { xs: '16px', md: '24px' } }}>
      <MessagesSnackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
        duration={3000}
      />
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: '15px', mb: '20px' }}>
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <CategorySidebar
            categories={categories}
            loading={categoriesLoading}
            selectedCategoryId={selectedCategoryId}
            fixedHeight={sidebarFixedHeight}
            onCategorySelect={handleCategorySelect}
          />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '15px' }}>
          <Box sx={{ height: `${STOREFRONT_HEADER_HEIGHT}px` }}>
            <StorefrontHeader
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onSearchSubmit={handleSearch}
              cartItemCount={cartItemCount}
              onCartClick={handleCartClick}
              cartDisabled={!activeOrder}
            />
          </Box>
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <CategorySidebar
              categories={categories}
              loading={categoriesLoading}
              selectedCategoryId={selectedCategoryId}
              onCategorySelect={handleCategorySelect}
            />
          </Box>
          {heroBanners.length > 0 && (
            <Box sx={{ flex: 1 }}>
              <HeroBannerCarousel banners={heroBanners}
                fixedHeight={heroHeight} />
            </Box>
          )}
        </Box>
      </Box>

      {(promoLeftBanners.length > 0 || promoRightBanners.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '15px', mb: '32px' }}>
          {promoLeftBanners.length > 0 && <PromoBannerCard banners={promoLeftBanners} />}
          {promoRightBanners.length > 0 && <PromoBannerCard banners={promoRightBanners} />}
        </Box>
      )}

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', mb: '20px' }}>
          <Typography sx={{ fontWeight: 600, fontSize: '20px', lineHeight: 1.2 }}>
            {selectedCategoryId
              ? categories.find((c) => c.id === selectedCategoryId)?.name ?? t('editorsPick')
              : t('editorsPick')}
          </Typography>
          {!selectedCategoryId && (
            <Typography
              sx={{ color: theme.palette.custom.gray[400], fontStyle: 'italic', fontSize: '14px' }}
            >
              {t('chosenForYou')}
            </Typography>
          )}
        </Box>

        {productsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '48px' }}>
            <CircularProgress />
          </Box>
        )}

        {!productsLoading && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: { xs: '12px', md: '16px', lg: '18px' },
            }}
          >
            {products.map((product) => {
              const basketQuantity = activeOrder?.items?.find((i) => i.itemId === product.id)?.quantity ?? 0;
              return (
                <ProductCard
                  key={product.id}
                  product={product}
                  currencyCode={currencyCode}
                  basketQuantity={basketQuantity}
                  onAddToOrder={handleAddToOrder}
                />
              );
            })}
          </Box>
        )}

        {!productsLoading && products.length === 0 && (
          <Typography
            sx={{ color: theme.palette.custom.gray[400], textAlign: 'center', py: '48px', fontSize: '14px' }}
          >
            {t('noProductsFound')}
          </Typography>
        )}

        {!productsLoading && productTotalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={productTotalPages}
              page={productPage}
              onChange={handleProductsPageChange}
              shape="rounded"
              color="primary"
              size="medium"
            />
          </Box>
        )}

        {!productsLoading && productTotalCount > 0 && (
          <Typography sx={{ color: theme.palette.custom.gray[400], textAlign: 'center', mt: 1, fontSize: '12px' }}>
            {productTotalCount}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default StorefrontPage;
